const nodejsCheatsheet = {
  id: "nodejs",
  title: "Node.js Cheatsheet",
  description:
    "A comprehensive guide to Node.js including core modules, async programming, file system operations, and best practices",
  language: "javascript",
  category: "Programming & Backend",
  level: "All Levels",
  sections: [
    {
      title: "Core Modules",
      items: [
        {
          title: "HTTP Module",
          content: "Create HTTP servers and make HTTP requests",
          code: `// Basic HTTP Server
const http = require('http');

const server = http.createServer((req, res) => {
  res.writeHead(200, { 'Content-Type': 'text/plain' });
  res.end('Hello World\\n');
});

server.listen(3000, () => {
  console.log('Server running at http://localhost:3000/');
});

// HTTP Request
http.get('http://api.example.com/data', (resp) => {
  let data = '';
  resp.on('data', (chunk) => { data += chunk; });
  resp.on('end', () => { console.log(JSON.parse(data)); });
}).on('error', (err) => {
  console.log("Error: " + err.message);
});`,
          note: "The HTTP module is fundamental for creating web servers and making HTTP requests",
        },
        {
          title: "File System (fs)",
          content: "Handle file operations synchronously and asynchronously",
          code: `const fs = require('fs');

// Asynchronous file read
fs.readFile('file.txt', 'utf8', (err, data) => {
  if (err) throw err;
  console.log(data);
});

// Synchronous file read
const data = fs.readFileSync('file.txt', 'utf8');

// Write file
fs.writeFile('file.txt', 'Hello World!', (err) => {
  if (err) throw err;
  console.log('File written successfully');
});

// Check if file exists
fs.access('file.txt', fs.constants.F_OK, (err) => {
  console.log(\`\${err ? 'does not exist' : 'exists'}\`);
});

// Working with directories
fs.mkdir('newDir', { recursive: true }, (err) => {
  if (err) throw err;
});`,
          note: "Always use asynchronous methods unless there's a specific reason to block execution",
        },
      ],
    },
    {
      title: "Express.js Basics",
      items: [
        {
          title: "Basic Express Server",
          content: "Setting up an Express.js server with routes",
          code: `const express = require('express');
const app = express();

// Middleware
app.use(express.json());
app.use(express.urlencoded({ extended: true }));

// Basic routing
app.get('/', (req, res) => {
  res.send('Hello World!');
});

// Route with parameters
app.get('/users/:id', (req, res) => {
  res.send(\`User ID: \${req.params.id}\`);
});

// POST request handling
app.post('/api/users', (req, res) => {
  const { username, email } = req.body;
  // Process the data
  res.json({ message: 'User created' });
});

// Error handling middleware
app.use((err, req, res, next) => {
  console.error(err.stack);
  res.status(500).send('Something broke!');
});

app.listen(3000, () => {
  console.log('Server is running on port 3000');
});`,
          note: "Express.js is the most popular web framework for Node.js",
        },
      ],
    },
    {
      title: "Async Programming",
      items: [
        {
          title: "Promises and Async/Await",
          content: "Modern asynchronous programming patterns",
          code: `// Promise example
function fetchData() {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve({ data: 'Success' });
    }, 1000);
  });
}

// Using Promise
fetchData()
  .then(result => console.log(result))
  .catch(error => console.error(error));

// Async/Await
async function getData() {
  try {
    const result = await fetchData();
    console.log(result);
  } catch (error) {
    console.error(error);
  }
}

// Promise.all example
const promises = [
  fetch('https://api.example.com/data1'),
  fetch('https://api.example.com/data2'),
  fetch('https://api.example.com/data3')
];

Promise.all(promises)
  .then(results => results.map(r => r.json()))
  .then(data => console.log(data));`,
          note: "Async/Await provides cleaner syntax for handling promises",
        },
      ],
    },
    {
      title: "Events and Streams",
      items: [
        {
          title: "Event Emitter",
          content: "Node.js built-in event handling",
          code: `const EventEmitter = require('events');

class MyEmitter extends EventEmitter {}
const myEmitter = new MyEmitter();

// Event listener
myEmitter.on('event', (data) => {
  console.log('Event occurred:', data);
});

// Emit event
myEmitter.emit('event', { message: 'Hello!' });

// One-time event listener
myEmitter.once('oneTime', () => {
  console.log('This will only run once');
});`,
          note: "EventEmitter is the backbone of Node.js asynchronous event-driven architecture",
        },
        {
          title: "Streams",
          content: "Handling streaming data",
          code: `const fs = require('fs');

// Read stream
const readStream = fs.createReadStream('input.txt');
readStream.on('data', (chunk) => {
  console.log('Received chunk:', chunk);
});

// Write stream
const writeStream = fs.createWriteStream('output.txt');
writeStream.write('Hello Stream!');
writeStream.end();

// Pipe streams
const readableStream = fs.createReadStream('input.txt');
const writableStream = fs.createWriteStream('output.txt');
readableStream.pipe(writableStream);

// Transform stream
const { Transform } = require('stream');
const upperCaseTransform = new Transform({
  transform(chunk, encoding, callback) {
    callback(null, chunk.toString().toUpperCase());
  }
});`,
          note: "Streams are useful for handling large amounts of data efficiently",
        },
      ],
    },
    {
      title: "Database Integration",
      items: [
        {
          title: "MongoDB with Mongoose",
          content: "MongoDB ODM for Node.js",
          code: `const mongoose = require('mongoose');

// Connect to MongoDB
mongoose.connect('mongodb://localhost/myapp', {
  useNewUrlParser: true,
  useUnifiedTopology: true
});

// Define Schema
const userSchema = new mongoose.Schema({
  name: String,
  email: { type: String, required: true, unique: true },
  age: Number,
  createdAt: { type: Date, default: Date.now }
});

// Create Model
const User = mongoose.model('User', userSchema);

// Create document
const user = new User({
  name: 'John Doe',
  email: 'john@example.com',
  age: 30
});

// Save document
await user.save();

// Find documents
const users = await User.find({ age: { $gte: 18 } });

// Update document
await User.updateOne(
  { email: 'john@example.com' },
  { $set: { age: 31 } }
);`,
          note: "Mongoose provides a straight-forward, schema-based solution for modeling application data",
        },
      ],
    },
    {
      title: "Security Best Practices",
      items: [
        {
          title: "Security Middleware",
          content: "Essential security middleware and practices",
          code: `const express = require('express');
const helmet = require('helmet');
const rateLimit = require('express-rate-limit');
const cors = require('cors');

const app = express();

// Basic security headers
app.use(helmet());

// Rate limiting
const limiter = rateLimit({
  windowMs: 15 * 60 * 1000, // 15 minutes
  max: 100 // limit each IP to 100 requests per windowMs
});
app.use(limiter);

// CORS configuration
app.use(cors({
  origin: 'https://yourdomain.com',
  methods: ['GET', 'POST'],
  allowedHeaders: ['Content-Type', 'Authorization']
}));

// Input validation
const { body, validationResult } = require('express-validator');

app.post('/api/user',
  body('email').isEmail(),
  body('password').isLength({ min: 6 }),
  (req, res) => {
    const errors = validationResult(req);
    if (!errors.isEmpty()) {
      return res.status(400).json({ errors: errors.array() });
    }
    // Process valid input
  }
);`,
          note: "Security should be a top priority in any Node.js application",
        },
      ],
    },
    {
      title: "Testing",
      items: [
        {
          title: "Unit Testing with Jest",
          content: "Writing and running tests with Jest",
          code: `// Function to test
function sum(a, b) {
  return a + b;
}

// Test suite
describe('sum function', () => {
  test('adds 1 + 2 to equal 3', () => {
    expect(sum(1, 2)).toBe(3);
  });

  test('handles negative numbers', () => {
    expect(sum(-1, -2)).toBe(-3);
  });
});

// Async test
describe('async operations', () => {
  test('fetches user data', async () => {
    const data = await fetchUserData(1);
    expect(data).toHaveProperty('name');
  });
});

// Mock function
const mockCallback = jest.fn(x => 42 + x);
forEach([0, 1], mockCallback);

expect(mockCallback.mock.calls.length).toBe(2);`,
          note: "Jest is a popular testing framework that comes with built-in assertion libraries and mocking capabilities",
        },
      ],
    },
    {
      title: "Node.js Commands",
      items: [
        {
          title: "Node.js CLI Commands",
          content: "Essential Node.js command line interface commands",
          code: `// Start a Node.js application
node app.js              // Run a JavaScript file
node --version          // Check Node.js version
node -v                 // Short version for --version
node --inspect app.js   // Debug mode with Chrome DevTools
node --trace-warnings   // Show stack traces for warnings
node -e "console.log('Hello')"  // Execute code directly

// REPL (Read-Eval-Print-Loop) Commands
node                    // Start REPL
.help                   // List all REPL commands
.exit                   // Exit REPL
.break                  // Exit multiline expression
.clear                  // Clear REPL context
.save filename          // Save REPL session to file
.load filename          // Load file into REPL session`,
          note: "Node.js CLI commands are essential for running and debugging applications",
        },
        {
          title: "NPM Commands",
          content: "Common npm (Node Package Manager) commands",
          code: `// Project Initialization
npm init                // Initialize a new project
npm init -y             // Initialize with default values

// Package Installation
npm install package     // Install a package
npm i package           // Short version of install
npm install -g package  // Install globally
npm install --save-dev  // Install as dev dependency
npm install --production // Install only production dependencies

// Package Management
npm uninstall package   // Remove a package
npm update              // Update packages
npm update package      // Update specific package
npm outdated            // Check outdated packages
npm list                // List installed packages
npm list -g             // List global packages
npm audit               // Security audit
npm audit fix           // Fix security issues

// Script Running
npm start               // Run start script
npm test                // Run test script
npm run script-name     // Run custom script

// Package Publishing
npm login               // Login to npm registry
npm publish            // Publish package
npm version patch      // Bump patch version
npm version minor      // Bump minor version
npm version major      // Bump major version

// Configuration
npm config list         // List config settings
npm config set key value // Set config value
npm cache clean --force // Clear npm cache`,
          note: "npm is the default package manager for Node.js and is essential for managing dependencies",
        },
        {
          title: "Yarn Commands",
          content: "Alternative to npm - Yarn package manager commands",
          code: `// Installation and Setup
yarn                    // Install all dependencies
yarn add package        // Add dependency
yarn add -D package     // Add dev dependency
yarn global add package // Install package globally

// Package Management
yarn remove package     // Remove dependency
yarn upgrade            // Upgrade packages
yarn upgrade package    // Upgrade specific package
yarn outdated           // Check outdated packages
yarn list              // List installed packages

// Script Running
yarn start             // Run start script
yarn test              // Run test script
yarn run script-name   // Run custom script

// Cache Management
yarn cache list        // List cached packages
yarn cache clean       // Clear cache

// Workspace Commands
yarn workspace ws run  // Run command in workspace
yarn workspaces run    // Run in all workspaces`,
          note: "Yarn is an alternative to npm with some additional features and different command syntax",
        },
      ],
    },
    {
      title: "Process and Environment",
      items: [
        {
          title: "Process Commands",
          content: "Node.js process and environment management",
          code: `// Process Information
process.version        // Node.js version
process.env           // Environment variables
process.argv          // Command line arguments
process.pid           // Process ID
process.platform      // Operating system platform
process.cwd()         // Current working directory

// Environment Variables
process.env.NODE_ENV  // Environment mode
process.env.PORT      // Port number
process.env.PATH      // System path

// Process Events
process.on('exit', (code) => {
  // Handle process exit
});

process.on('uncaughtException', (err) => {
  // Handle uncaught exceptions
});

// Exit Process
process.exit(0)       // Exit successfully
process.exit(1)       // Exit with error

// Memory Usage
process.memoryUsage() // Get memory usage stats
process.cpuUsage()    // Get CPU usage stats`,
          note: "Understanding process commands is crucial for application management and debugging",
        },
        {
          title: "Debug Commands",
          content: "Debugging commands and flags",
          code: `// Debugging Flags
node --inspect         // Enable inspector
node --inspect-brk     // Break on first line
node --trace-warnings  // Show warning stack traces
node --trace-sync-io   // Warn about sync I/O
node --trace-events    // Trace event listener

// Environment Debug
DEBUG=* node app.js    // Enable all debug logs
DEBUG=app:* node app.js // Debug specific module

// Node.js Debugger
node debug app.js      // Start legacy debugger
debugger;             // Add breakpoint in code

// Performance Flags
node --prof           // Generate V8 profiler data
node --trace-gc       // Trace garbage collection
node --max-old-space-size=4096  // Set max memory`,
          note: "Debugging commands help in troubleshooting and optimizing Node.js applications",
        },
      ],
    },
  ],
};

export default nodejsCheatsheet;
